import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { AlertTriangle, Building2, TrendingUp, Users } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'

export const Route = createFileRoute('/master/')({
  component: MasterDashboard,
})

function MasterDashboard() {
  const { data: companies = [], isLoading } = useQuery({
    queryKey: ['master_companies'],
    queryFn: async () => {
      const { data, error } = await supabase.from('company_settings').select('*')

      if (error) throw error
      return data
    },
  })

  // Cálculos de SaaS
  const planPrices: Record<string, number> = {
    p1: 97,
    p2: 147,
    p3: 197,
  }

  const activeCompanies = companies.filter((c) => c.subscription_status === 'ACTIVE')
  const trialingCompanies = companies.filter((c) => c.subscription_status === 'TRIALING')
  const pastDueCompanies = companies.filter(
    (c) => c.subscription_status === 'PAST_DUE' || c.subscription_status === 'CANCELED'
  )

  // MRR (Monthly Recurring Revenue) - Apenas ativos
  const currentMRR = activeCompanies.reduce((acc, comp) => {
    return acc + (planPrices[comp.plan_id || 'p1'] || 0)
  }, 0)

  if (isLoading) {
    return <div className="p-12 flex justify-center text-amber-500">Carregando métricas...</div>
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col gap-2">
        <h2 className="text-3xl font-bold text-white tracking-tight">Visão Geral</h2>
        <p className="text-slate-400">Acompanhe o crescimento e saúde financeira do seu SaaS.</p>
      </div>

      {/* Cards de Métricas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* MRR */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 rounded-bl-full -mr-8 -mt-8"></div>
          <div className="flex justify-between items-start relative z-10">
            <div>
              <p className="text-sm font-medium text-slate-400 mb-1">Receita Mensal (MRR)</p>
              <h3 className="text-3xl font-black text-white">
                R$ {currentMRR.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </h3>
            </div>
            <div className="p-3 bg-amber-500/20 text-amber-500 rounded-xl">
              <TrendingUp size={24} />
            </div>
          </div>
        </div>

        {/* Total Inquilinos */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-bl-full -mr-8 -mt-8"></div>
          <div className="flex justify-between items-start relative z-10">
            <div>
              <p className="text-sm font-medium text-slate-400 mb-1">Total de Lava-Jatos</p>
              <h3 className="text-3xl font-black text-white">{companies.length}</h3>
            </div>
            <div className="p-3 bg-blue-500/20 text-blue-500 rounded-xl">
              <Building2 size={24} />
            </div>
          </div>
        </div>

        {/* Em Período de Teste */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-bl-full -mr-8 -mt-8"></div>
          <div className="flex justify-between items-start relative z-10">
            <div>
              <p className="text-sm font-medium text-slate-400 mb-1">Em Período de Teste</p>
              <h3 className="text-3xl font-black text-white">{trialingCompanies.length}</h3>
            </div>
            <div className="p-3 bg-emerald-500/20 text-emerald-500 rounded-xl">
              <Users size={24} />
            </div>
          </div>
        </div>

        {/* Inadimplentes / Cancelados */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/10 rounded-bl-full -mr-8 -mt-8"></div>
          <div className="flex justify-between items-start relative z-10">
            <div>
              <p className="text-sm font-medium text-slate-400 mb-1">Inadimplentes/Churn</p>
              <h3 className="text-3xl font-black text-white">{pastDueCompanies.length}</h3>
            </div>
            <div className="p-3 bg-red-500/20 text-red-500 rounded-xl">
              <AlertTriangle size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Lista Rápida */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl">
        <h3 className="text-lg font-bold text-white mb-4">Últimos Lava-Jatos Adicionados</h3>
        <div className="space-y-3">
          {companies.slice(0, 5).map((c) => (
            <div
              key={c.id}
              className="flex justify-between items-center p-4 bg-slate-950/50 rounded-xl border border-slate-800"
            >
              <div>
                <p className="font-bold text-white">{c.name || 'Sem Nome'}</p>
                <p className="text-sm text-slate-500">
                  {c.cnpj || 'Sem CNPJ'} • Plano {c.plan_id?.toUpperCase() || 'P1'}
                </p>
              </div>
              <div
                className={`px-3 py-1 rounded-full text-xs font-bold border ${
                  c.subscription_status === 'ACTIVE'
                    ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                    : c.subscription_status === 'TRIALING'
                      ? 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                      : 'bg-red-500/10 text-red-400 border-red-500/20'
                }`}
              >
                {c.subscription_status}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
